import { Box, Skeleton, Stack } from "@mui/material";
import { Channel } from "stream-chat-react";
import type { Channel as StreamChannel } from "stream-chat";
import { ChatAttachmentRouter } from "Components/Chat/ChatAttachmentRouter";
import { ChatChannelView } from "Components/Chat/ChatChannelView";
import { ChatEditMessageInput } from "Components/Chat/ChatEditMessageInput";
import { ChatEmojiPicker } from "Components/Chat/ChatEmojiPicker";
import { ChatListLoadingIndicator } from "Components/Chat/ChatListLoadingIndicator";
import { ChatMessageCustomActionsList } from "Components/Chat/ChatMessageCustomActionsList";
import { ChatMessageReportProvider } from "Components/Chat/ChatMessageReportContext";
import { ChatMessageStatus } from "Components/Chat/ChatMessageStatus";
import { ChatMessageWithDisclosedName } from "Components/Chat/ChatMessageWithDisclosedName";
import { ChatReactionsListModal } from "Components/Chat/ChatReactionsListModal";
import { AttachmentSelectorWithPaymentRequest } from "Components/Chat/SendPaymentRequestButton";
import { IS_MANUAL_THREADING_ENABLED } from "Components/Chat/chatConstants";
import { Resources, useResource } from "Translations/Resources";

const PAGE_RESOURCES = Resources.Chat;

const HiddenRepliesCountButton = () => null;

/** Shown while the URL's channel is still being resolved — before `<Channel>` exists at all. */
const ChannelLoadingSkeleton = () => (
  <Stack flex={1}>
    <Box
      display="flex"
      alignItems="center"
      bgcolor="primary.main"
      p={2.5}
      gap={1}
      flexShrink={0}
    >
      <Skeleton
        variant="circular"
        width={40}
        height={40}
        sx={{ bgcolor: "rgba(255,255,255,0.2)" }}
      />
      <Skeleton
        variant="text"
        width={120}
        sx={{ bgcolor: "rgba(255,255,255,0.2)", fontSize: 16 }}
      />
    </Box>
  </Stack>
);

type Props = {
  isMobile: boolean;
  isChannelOpen: boolean;
  activeChannel: StreamChannel | null;
  onBack: () => void;
};

export const ChatMessagePanel = ({
  isMobile,
  isChannelOpen,
  activeChannel,
  onBack,
}: Props) => {
  const { t } = useResource();

  if (isMobile && !isChannelOpen) {
    return null;
  }

  return (
    <Box flex={1} height="100%" minWidth={0}>
      {activeChannel ? (
        <Channel
          channel={activeChannel}
          Attachment={ChatAttachmentRouter}
          Message={ChatMessageWithDisclosedName}
          AttachmentSelector={AttachmentSelectorWithPaymentRequest}
          EditMessageInput={ChatEditMessageInput}
          EmojiPicker={ChatEmojiPicker}
          LoadingIndicator={ChatListLoadingIndicator}
          ReactionsListModal={ChatReactionsListModal}
          MessageStatus={ChatMessageStatus}
          CustomMessageActionsList={ChatMessageCustomActionsList}
          // No "N replies" link under a message while manual threading is off — a multi-payer
          // request card shows its own "N payments" link instead.
          MessageRepliesCountButton={
            IS_MANUAL_THREADING_ENABLED ? undefined : HiddenRepliesCountButton
          }
        >
          <ChatMessageReportProvider>
            <ChatChannelView
              isMobile={isMobile}
              onBack={isMobile ? onBack : undefined}
            />
          </ChatMessageReportProvider>
        </Channel>
      ) : isChannelOpen ? (
        <ChannelLoadingSkeleton />
      ) : (
        <Stack
          alignItems="center"
          justifyContent="center"
          height="100%"
          color="text.secondary"
        >
          {t(PAGE_RESOURCES.SelectConversation)}
        </Stack>
      )}
    </Box>
  );
};
